/**
 * Support Chat API Service
 * Gestisce comunicazione real-time tra utenti e admin
 */

import { API_URL } from '../config';

export interface ChatSession {
  id: string;
  user_id: string;
  user_email?: string;
  admin_id?: string;
  status: 'open' | 'active' | 'closed';
  created_at: string;
  updated_at: string;
  closed_at?: string;
}

export interface ChatMessage {
  id: string;
  session_id: string;
  sender_id: string;
  sender_type: 'user' | 'admin';
  content: string;
  message_type: 'text' | 'image' | 'file';
  file_url?: string;
  file_name?: string;
  is_read: boolean;
  created_at: string;
}

export interface AdminStatus {
  online: boolean;
  count: number;
  admins: { email: string }[];
}

// Helper per headers
function getHeaders(userId: string, userEmail?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-User-ID': userId,
  };
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }
  return headers;
}

/**
 * Crea o recupera sessione attiva
 */
export async function getOrCreateSession(userId: string, userEmail?: string): Promise<ChatSession> {
  const response = await fetch(`${API_URL}/api/chat/sessions`, {
    method: 'POST',
    headers: getHeaders(userId, userEmail),
  });
  
  if (!response.ok) {
    throw new Error('Errore creazione sessione');
  }
  
  return response.json();
}

/**
 * Lista sessioni (admin vede tutte)
 */
export async function listSessions(userId: string, userEmail?: string, status?: string): Promise<ChatSession[]> {
  const url = new URL(`${API_URL}/api/chat/sessions`);
  if (status) url.searchParams.set('status', status);
  
  const response = await fetch(url.toString(), {
    headers: getHeaders(userId, userEmail),
  });
  
  if (!response.ok) {
    throw new Error('Errore lista sessioni');
  }
  
  return response.json();
}

/**
 * Recupera messaggi di una sessione
 */
export async function getMessages(sessionId: string, userId: string, limit = 100): Promise<ChatMessage[]> {
  const response = await fetch(`${API_URL}/api/chat/sessions/${sessionId}/messages?limit=${limit}`, {
    headers: getHeaders(userId),
  });
  
  if (!response.ok) {
    throw new Error('Errore recupero messaggi');
  }
  
  return response.json();
}

/**
 * Invia messaggio (REST, non WebSocket)
 */
export async function sendMessage(
  sessionId: string,
  userId: string,
  userEmail: string,
  content: string,
  messageType: 'text' | 'image' | 'file' = 'text',
  fileUrl?: string,
  fileName?: string
): Promise<ChatMessage> {
  const response = await fetch(`${API_URL}/api/chat/sessions/${sessionId}/messages`, {
    method: 'POST',
    headers: getHeaders(userId, userEmail),
    body: JSON.stringify({
      content,
      message_type: messageType,
      file_url: fileUrl,
      file_name: fileName,
    }),
  });
  
  if (!response.ok) {
    throw new Error('Errore invio messaggio');
  }
  
  return response.json();
}

/**
 * Marca messaggi come letti
 */
export async function markAsRead(sessionId: string, userId: string): Promise<void> {
  await fetch(`${API_URL}/api/chat/sessions/${sessionId}/read`, {
    method: 'POST',
    headers: getHeaders(userId),
  });
}

/**
 * Conta messaggi non letti
 */
export async function getUnreadCount(userId: string, userEmail?: string): Promise<number> {
  const response = await fetch(`${API_URL}/api/chat/unread`, {
    headers: getHeaders(userId, userEmail),
  });
  
  if (!response.ok) return 0;
  
  const data = await response.json();
  return data.count || 0;
}

/**
 * Aggiorna presenza (heartbeat)
 */
export async function updatePresence(userId: string, userEmail?: string): Promise<void> {
  await fetch(`${API_URL}/api/chat/presence`, {
    method: 'POST',
    headers: getHeaders(userId, userEmail),
  });
}

/**
 * Imposta offline
 */
export async function setOffline(userId: string): Promise<void> {
  await fetch(`${API_URL}/api/chat/presence`, {
    method: 'DELETE',
    headers: getHeaders(userId),
  });
}

/**
 * Controlla se admin è online
 */
export async function checkAdminOnline(): Promise<AdminStatus> {
  const response = await fetch(`${API_URL}/api/chat/admins/online`);
  
  if (!response.ok) {
    return { online: false, count: 0, admins: [] };
  }
  
  return response.json();
}

/**
 * Chiudi sessione
 */
export async function closeSession(sessionId: string, userId: string, userEmail?: string): Promise<void> {
  await fetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
    method: 'PUT',
    headers: getHeaders(userId, userEmail),
    body: JSON.stringify({ status: 'closed' }),
  });
}

/**
 * WebSocket URL per chat real-time
 */
export function getWebSocketUrl(sessionId: string, userId: string, userEmail?: string): string {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const baseUrl = API_URL.replace(/^https?:/, wsProtocol);
  let url = `${baseUrl}/api/chat/ws/${sessionId}?user_id=${encodeURIComponent(userId)}`;
  if (userEmail) {
    url += `&user_email=${encodeURIComponent(userEmail)}`;
  }
  return url;
}

/**
 * Converte file in base64 per upload
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

